/** Max length for agent/draft titles derived from prompts. */
export const MAX_TITLE_LENGTH = 80;

/**
 * Unicode word segmentation (UAX #29) — the platform's tokenizer, and the
 * reason this file no longer hand-rolls one out of character classes.
 *
 * A regex that keeps `[a-zA-Z0-9]` and blanks the rest does not know what a
 * word is: it split `I'm` into `I m` and `café` into `caf`. The segmenter
 * knows, because the apostrophe rules (WB6/WB7) and the letter classes are
 * the same ones every browser uses to select a word on double-click.
 * Contractions and possessives come back as single tokens, quotes and
 * punctuation around a word do not, and non-ASCII words survive intact.
 *
 * `en` is the locale the titles are written in. Segmentation is locale
 * sensitive, and pinning it keeps a title stable no matter which machine
 * derives it — a server default of `C`/`POSIX` must not change the result.
 */
const WORD_SEGMENTER = new Intl.Segmenter('en', { granularity: 'word' });

/**
 * Extract the first meaningful words from the prompt as a short title
 * (max 80 chars). Words are joined by single spaces, so the title normalizes
 * whatever spacing, punctuation, and line breaks the prompt used.
 *
 * The loop consumes the segmenter lazily and stops at the first word that
 * would overflow: a prompt can be an entire issue body, and only its opening
 * words can ever reach the title.
 */
export function generateFallbackTitle(prompt: string): string {
  let title = '';
  for (const { segment, isWordLike } of WORD_SEGMENTER.segment(prompt)) {
    // Non-word-like segments are the whitespace, punctuation, and emoji
    // between words. Dropping them is what turns `Fix: the @#$ path!!!` into
    // `Fix the path`.
    if (!isWordLike) continue;
    const candidate = title ? `${title} ${segment}` : segment;
    if (candidate.length > MAX_TITLE_LENGTH) break;
    title = candidate;
  }
  return title || 'Coding Task';
}
